import React, { useEffect, useState, useRef, useCallback } from 'react';
import { CheckCircleFilled, LoadingOutlined, CloudOutlined } from '@ant-design/icons';
import { useEditorContext } from '../contexts/EditorContext';
import styles from '../styles/index.module.scss';

const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

/**
 * 保存状态指示器（顶栏小胶囊）
 *
 * 状态机：idle → saving → saved → (3s后) 显示时间
 * - saving 立即展示，最少停留 600ms，避免闪一下
 * - saved 展示「已自动保存」，3 秒后改为「最近保存 HH:mm」
 */
export const SaveStatus = ({ compact = false }) => {
    const { saveState } = useEditorContext();
    const [displayStatus, setDisplayStatus] = useState('idle');
    const [showTime, setShowTime] = useState(false);
    const [lastSavedTime, setLastSavedTime] = useState(null);

    const savingStartRef = useRef(null);
    const pendingSavedRef = useRef(false);
    const minShowTimerRef = useRef(null);

    const transitionToSaved = useCallback(() => {
        setDisplayStatus('saved');
        setShowTime(false);
        setLastSavedTime(saveState.lastSavedTime);
        pendingSavedRef.current = false;
        savingStartRef.current = null;
    }, [saveState.lastSavedTime]);

    useEffect(() => {
        const status = saveState.status;
        if (status === 'saving') {
            clearTimeout(minShowTimerRef.current);
            pendingSavedRef.current = false;
            savingStartRef.current = Date.now();
            setDisplayStatus('saving');
        } else if (status === 'saved') {
            const MIN_SAVING_DISPLAY = 600;
            const elapsed = savingStartRef.current
                ? Date.now() - savingStartRef.current
                : MIN_SAVING_DISPLAY;
            const remaining = MIN_SAVING_DISPLAY - elapsed;
            if (remaining > 0) {
                pendingSavedRef.current = true;
                minShowTimerRef.current = setTimeout(() => {
                    if (pendingSavedRef.current) transitionToSaved();
                }, remaining);
            } else {
                transitionToSaved();
            }
        } else if (status === 'idle') {
            clearTimeout(minShowTimerRef.current);
            if (pendingSavedRef.current) {
                pendingSavedRef.current = false;
                savingStartRef.current = null;
            }
            setDisplayStatus('idle');
        }
        return () => clearTimeout(minShowTimerRef.current);
    }, [saveState.status, transitionToSaved]);

    useEffect(() => {
        if (displayStatus === 'saved' && lastSavedTime) {
            const timer = setTimeout(() => setShowTime(true), 3000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [displayStatus, lastSavedTime]);

    let icon = <CloudOutlined />;
    let text = '自动保存';
    let tone = styles.saveStatusIdle;

    if (displayStatus === 'saving') {
        icon = <LoadingOutlined />;
        text = '正在保存…';
        tone = styles.saveStatusSaving;
    } else if (lastSavedTime) {
        icon = <CheckCircleFilled />;
        text =
            displayStatus === 'saved' && !showTime
                ? '已自动保存'
                : `最近保存 ${formatTime(lastSavedTime)}`;
        tone = styles.saveStatusSaved;
    }

    return (
        <div className={`${styles.saveStatus} ${tone}`} title={text}>
            <span className={styles.saveStatusIcon}>{icon}</span>
            {!compact && <span className={styles.saveStatusText}>{text}</span>}
        </div>
    );
};

export default SaveStatus;
